import type { ReactNode } from 'react';
import { useFormat, type Formatter } from '@paige/format';
import { PeriodChart } from '../period/PeriodChart';
import { beforeWords } from '../period/compare';
import { periodLabel, type Period } from '../period/period';
import type { ChartSeries } from '../period/period-chart-geometry';
import { salesMetrics, type CustomerSummary, type SalesMetric } from './customer-types';
import { changeOf, compact, metricWord, type Change } from './customer-words';
import './customers.css';

export interface SalesPictureProps {
  summary: CustomerSummary;
  /** Names a figure with its period: "Card money, last 30 days". */
  label: (what: string) => string;
  /** The series drawn large. */
  metric: SalesMetric;
  onMetric: (metric: SalesMetric) => void;
  /** A click on a mark: the page narrows to those days. */
  onPick: (bucket: Period) => void;
  /** Shown after a pick: the way back to the period it was made from. */
  back?: { label: string; onBack: () => void } | null | undefined;
  /** The numbers on screen are the last answer while the next loads. */
  stale?: boolean | undefined;
}

const whole = (f: Formatter) => (n: number) => f.int(n);

/** The series for the chart: one at a time, so the axis and the marks share one scale. */
function seriesOf(summary: CustomerSummary, metric: SalesMetric, f: Formatter): ChartSeries {
  const values = summary.series.map((p) => p[metric]);
  if (metric === 'revenue') {
    return { key: metric, label: 'Card money', values, kind: 'bars', format: f.moneyWhole };
  }
  return {
    key: metric,
    label: metric === 'pages' ? 'Pages read' : 'New paying companies',
    values,
    kind: 'line',
    format: metric === 'pages' ? compact : whole(f),
  };
}

const previousKey = {
  revenue: 'previousRevenue',
  newPaying: 'previousNewPaying',
  pages: 'previousPages',
} as const;

/** The period before, bucket for bucket, for the faint dashed line: "The 30 days before". */
function previousOf(summary: CustomerSummary, metric: SalesMetric) {
  const words = beforeWords(summary.period.days);
  return {
    label: words.charAt(0).toUpperCase() + words.slice(1),
    values: summary.series.map((p) => p[previousKey[metric]]),
  };
}

function ChangeWords({ change }: { change: Change }): ReactNode {
  return (
    <span className={`ad-cu-change ad-cu-change--${change.tone}`}>
      <span aria-hidden="true">{change.arrow}</span> {change.text}
    </span>
  );
}

/** One of the two figures beside the headline. */
function Figure(props: { label: string; value: string; change: Change }): ReactNode {
  return (
    <div className="ad-cu-figure">
      <span className="ad-cu-figure__label">{props.label}</span>
      <span className="ad-cu-figure__value">{props.value}</span>
      <ChangeWords change={props.change} />
    </div>
  );
}

function MetricChips({ metric, onMetric }: Pick<SalesPictureProps, 'metric' | 'onMetric'>) {
  return (
    <div className="ad-cu-chips" role="group" aria-label="Draw in the chart">
      {salesMetrics.map((m) => (
        <button
          key={m}
          type="button"
          className="ad-cu-chip"
          aria-pressed={metric === m}
          onClick={() => onMetric(m)}
        >
          {metricWord[m]}
        </button>
      ))}
    </div>
  );
}

const bucketWord = { day: 'day', week: 'week', month: 'month' } as const;

/** The headline and the two figures beside it, each with its period and its change in words. */
function Figures({ summary: s, label }: SalesPictureProps): ReactNode {
  const f = useFormat();
  return (
    <div className="ad-cu-sales__top">
      <div className="ad-cu-headline">
        <span className="ad-cu-headline__label">{label('Card money')}</span>
        <strong className="ad-cu-headline__value">{f.money(s.revenue)}</strong>
        <ChangeWords change={changeOf(s.revenue, s.previousRevenue, f.money)} />
      </div>
      <Figure
        label={label('New paying companies')}
        value={f.int(s.newPaying)}
        change={changeOf(s.newPaying, s.previousNewPaying, whole(f))}
      />
      <Figure
        label={label('Pages read')}
        value={f.int(s.pages)}
        change={changeOf(s.pages, s.previousPages, whole(f))}
      />
    </div>
  );
}

/**
 * The sales picture (Dustin, 2026-10-03): the card money of the period, large, against the period before
 * in words; new paying companies and pages read beside it; and the period drawn by day, week or month,
 * one series at a time. A click on a mark narrows the whole page to those days.
 */
export function SalesPicture(props: SalesPictureProps): ReactNode {
  const { summary: s, metric, onMetric, back } = props;
  const f = useFormat();
  const series = seriesOf(s, metric, f);
  const period = { from: s.period.from, until: s.period.until };
  return (
    <section
      className={`ad-cu-sales${props.stale ? ' ad-cu-stale' : ''}`}
      aria-label="The sales picture"
      aria-busy={props.stale ? true : undefined}
    >
      <Figures {...props} />
      <div className="ad-cu-sales__bar">
        <MetricChips metric={metric} onMetric={onMetric} />
        {back ? (
          <button type="button" className="ad-cu-back" onClick={back.onBack}>
            <span aria-hidden="true">←</span> Back to {back.label}
          </button>
        ) : null}
      </div>
      <PeriodChart
        label={`${series.label} by ${bucketWord[s.bucket]}, ${periodLabel(period)}`}
        buckets={s.series}
        series={[series]}
        previous={previousOf(s, metric)}
        {...(s.series.length > 1 ? { onPick: props.onPick } : {})}
        height={240}
        empty={`No ${series.label.toLowerCase()} in this period.`}
      />
    </section>
  );
}
